import type {
    InventoryResource} from "@@/shared/acquire/resources/inventory-resource";
import {
    type InventoryResourceType,
} from "@@/shared/acquire/resources/inventory-resource";
import type {
    ResourceAccommodation,
    ResourceAccommodationFactoryParams} from "@@/shared/acquire/resources/resource-accommodation/resource-accommodation";
import {
    resourceAccommodationFactory,
    resourceAccommodationZodObject,
} from "@@/shared/acquire/resources/resource-accommodation/resource-accommodation";
import type { ResourceFoundationOf } from "@@/shared/acquire/resources/resource-foundation-helpers";
import {
    resourceFoundationIdFactory,
    resourceFoundationIdZodSchema,
} from "@@/shared/acquire/resources/resource-foundation-id";
import { assertNever } from "@@/shared/assert-never";
import { assertTypeMatch } from "@@/utilities/type-helpers";
import type { Except, IsEqual } from "type-fest";
import { z } from "zod";
import type {
    ResourceGeneric,
    ResourceGenericFactoryParams} from "./resource-generic";
import {
    resourceGenericFactory,
    resourceGenericZodObject,
} from "./resource-generic";
import type {
    ResourceReserved,
    ResourceReservedFactoryParams} from "./resource-reserved";
import {
    resourceReservedFactory,
    resourceReservedZodObject,
} from "./resource-reserved";
import type {
    ResourceTable,
    ResourceTableFactoryParams} from "./resource-table";
import {
    resourceTableFactory,
    resourceTableZodObject,
} from "./resource-table";

const resourceFoundationZodObject = z.object({
    _id: resourceFoundationIdZodSchema,
    _type: z.literal("RESOURCE_FOUNDATION"),
    data: z.discriminatedUnion("_type", [
        resourceGenericZodObject.omit({ _id: true }),
        resourceTableZodObject.omit({ _id: true }),
        resourceAccommodationZodObject.omit({ _id: true }),
        resourceReservedZodObject.omit({ _id: true }),
    ]),
});
type ResourceFoundation = z.infer<typeof resourceFoundationZodObject>;

type ResourceFoundationData<T extends ResourceFoundation["data"]["_type"]> =
    Extract<ResourceFoundation["data"], { _type: T }>;

// make sure we've got all type types from inventory resource types
// covered in the ResourceFoundation.type property
assertTypeMatch<
    IsEqual<InventoryResourceType, ResourceFoundation["data"]["_type"]>
>();

function resourceFoundationFactory(
    ofType: "RESOURCE_ACCOMMODATION",
    params: Except<ResourceAccommodationFactoryParams, "_id">,
): ResourceFoundationOf<ResourceAccommodation>;
function resourceFoundationFactory(
    ofType: "RESOURCE_GENERIC",
    params: Except<ResourceGenericFactoryParams, "_id">,
): ResourceFoundationOf<ResourceGeneric>;
function resourceFoundationFactory(
    ofType: "RESOURCE_TABLE",
    params: Except<ResourceTableFactoryParams, "_id">,
): ResourceFoundationOf<ResourceTable>;
function resourceFoundationFactory(
    ofType: "RESOURCE_RESERVED",
    params: Except<ResourceReservedFactoryParams, "_id">,
): ResourceFoundationOf<ResourceReserved>;
function resourceFoundationFactory(
    ofType: InventoryResourceType,
    params:
        | Except<ResourceAccommodationFactoryParams, "_id">
        | Except<ResourceGenericFactoryParams, "_id">
        | Except<ResourceTableFactoryParams, "_id">
        | Except<ResourceReservedFactoryParams, "_id">,
): ResourceFoundationOf<InventoryResource>;
function resourceFoundationFactory(
    ofType: InventoryResourceType,
    params:
        | Except<ResourceAccommodationFactoryParams, "_id">
        | Except<ResourceGenericFactoryParams, "_id">
        | Except<ResourceTableFactoryParams, "_id">
        | Except<ResourceReservedFactoryParams, "_id">,
): ResourceFoundationOf<InventoryResource> {
    const data = (() => {
        switch (ofType) {
            case "RESOURCE_GENERIC": {
                const _params: ResourceGenericFactoryParams = params;
                return resourceGenericFactory(_params);
            }
            case "RESOURCE_TABLE": {
                const _params: ResourceTableFactoryParams = params;
                return resourceTableFactory(_params);
            }
            case "RESOURCE_ACCOMMODATION": {
                const _params = params as ResourceAccommodationFactoryParams;
                return resourceAccommodationFactory(_params);
            }
            case "RESOURCE_RESERVED": {
                const _params: ResourceTableFactoryParams = params;
                return resourceReservedFactory(_params);
            }
            default:
                assertNever(ofType);
        }
    })();
    const { _id, ...foundationData } = data;
    const foundation: ResourceFoundationOf<InventoryResource> = {
        _id: resourceFoundationIdFactory(),
        _type: "RESOURCE_FOUNDATION",
        data: foundationData,
    };
    return foundation;
}
export { resourceFoundationFactory, resourceFoundationZodObject };
export type { ResourceFoundation, ResourceFoundationData };
